/**
 * Query hooks of the inventory report workbench (batch 8 T14c, #3036).
 *
 * The wire types below are the browser's OWN typed contract of the seven `GET /inventory/reports/...` routes of
 * batch 8 T14a (`services/staff-api/src/lib/inventory-report-http/`). They are written out here, never imported from
 * the backend, and a test checks them against the documented fields. Money is exact text with four decimals of a cent
 * (`"1402.0000"`); `...Cents` is the integer the server rounded once; `{ available: false, reason }` is unknown and is
 * never shown as 0. The portal never sums, rounds or re-derives a financial figure.
 *
 * Every report query belongs to one OWNER (session, tenant, user and the permissions that change the answer), which
 * is part of its key. A request the owner may not make is not sent at all (`enabled` is false and the queryFn
 * re-checks the live store), and a different owner can never read, or be shown while loading, what another fetched.
 * Reads only: there is no mutation in this file.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';
import {
  OwnerChangedError,
  ownerKey,
  readReportsAccess,
  useReportsAccess,
  useReportsCacheGuard,
  type ReportsAccess,
} from '@/features/inventory/reports/reportsSession';

/* ------------------------------------------------------------------ wire contracts */

export type WireMetric<T> =
  | { available: true; value: T; provenance: string }
  | { available: false; reason: string; provenance: string; detail?: string };

export interface WirePagination {
  /** Rows of the WHOLE result (a page past the end still reports it). */
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface WireWindow {
  fromDate: string;
  toDate: string;
  todayDate: string;
  timeZone: string;
  calendarDays: number;
  startUtc: string;
  endUtc: string;
}

export interface WireValueSource {
  value: 'fifo_layers' | 'frozen_invoice_lines';
  legacyMovementCost: 'not_used';
  unknownCost: 'never_valued';
}

export interface WireReconciliation {
  totalCents4: string;
  totalCents: number;
  sumOfRoundedRowsCents: number;
  roundingDifferenceCents: number;
}

export type AbcClass = 'A' | 'B' | 'C' | 'N' | 'U';

export interface WireAbcUnitsRow {
  partId: string;
  sku: string;
  name: string | null;
  categoryId: string | null;
  class: AbcClass | null;
  rank: number | null;
  netUnits: number;
  sharePercent: number | null;
  cumulativeBeforePercent: number | null;
  cumulativePercent: number | null;
}
export interface WireAbcFinancialRow extends WireAbcUnitsRow {
  valueCents4: string | null;
  valueCents: number | null;
  unknownReason: 'cost_unconfirmed' | 'no_revenue_identity' | null;
}
interface WireAbcCommon {
  report: 'abc';
  window: WireWindow;
  thresholds: { aPercent: number; bPercent: number };
  classification: WireMetric<{ rankedParts: number }>;
  rankedTotalUnits: number;
  pagination: WirePagination;
}
export interface WireAbcUnits extends WireAbcCommon {
  basis: 'units';
  financial: false;
  classes: Record<AbcClass, { parts: number; units: number; sharePercent: number | null }>;
  rows: WireAbcUnitsRow[];
}
export interface WireAbcFinancial extends WireAbcCommon {
  basis: 'cost' | 'revenue';
  financial: true;
  source: WireValueSource;
  rankedTotalCents4: string;
  classes: Record<AbcClass, { parts: number; units: number; sharePercent: number | null; valueCents4: string }>;
  rounding: WireReconciliation;
  rows: WireAbcFinancialRow[];
}
export type WireAbc = WireAbcUnits | WireAbcFinancial;

interface WireTurnoverGroupCommon {
  key: string;
  parts: number;
  noHistoryParts: number;
  unavailableParts: number;
  turnover: WireMetric<number>;
  daysInInventory: WireMetric<number>;
}
export interface WireTurnoverUnitsGroup extends WireTurnoverGroupCommon {
  numeratorUnits: WireMetric<number>;
  edgeSumUnits: WireMetric<number>;
}
export interface WireTurnoverCostGroup extends WireTurnoverGroupCommon {
  numeratorCents4: WireMetric<string>;
  edgeSumCents4: WireMetric<string>;
}
interface WireTurnoverCommon {
  report: 'turnover';
  groupBy: 'part' | 'category';
  calendarDays: number;
  window: WireWindow;
  edges: { start: string; end: string };
  pagination: WirePagination;
}
export interface WireTurnoverUnits extends WireTurnoverCommon {
  basis: 'units';
  financial: false;
  total: WireTurnoverUnitsGroup;
  groups: WireTurnoverUnitsGroup[];
}
export interface WireTurnoverCost extends WireTurnoverCommon {
  basis: 'cost';
  financial: true;
  source: WireValueSource;
  total: WireTurnoverCostGroup;
  groups: WireTurnoverCostGroup[];
}
export type WireTurnover = WireTurnoverUnits | WireTurnoverCost;

export type DeadSlowStatus = 'dead' | 'slow' | 'active' | 'no_history';

export interface WireDeadSlowRow {
  partId: string;
  sku: string;
  name: string;
  categoryId: string;
  status: DeadSlowStatus;
  statusReason: 'no_consumption_history' | 'age_unavailable' | 'zero_window_usage' | null;
  onHand: number;
  oldestRemainingLayerDate: string | null;
  newestRemainingLayerDate: string | null;
  oldestRemainingAgeDays: WireMetric<number>;
  ageIsLowerBound: boolean;
  lastConsumptionAt: string | null;
  consumptionRecencyDays: WireMetric<number>;
  hasAgedStock: boolean;
  windowUnits: number;
  coverageDays: WireMetric<number>;
  unbackedQuantity: number;
}
export interface WireDeadSlowFinancialRow extends WireDeadSlowRow {
  unconfirmedQuantity: number;
  valueAtRiskCents4: string;
  valueAtRiskCents: number;
}
interface WireDeadSlowCommon {
  report: 'dead-and-slow';
  params: { deadDays: number; slowCoverageDays: number; windowDays: number; now: string };
  window: WireWindow;
  pagination: WirePagination;
}
interface WireDeadSlowSummaryCommon {
  counts: Record<DeadSlowStatus, number>;
  agedStockParts: number;
}
export interface WireDeadSlowUnits extends WireDeadSlowCommon {
  financial: false;
  summary: WireDeadSlowSummaryCommon;
  rows: WireDeadSlowRow[];
}
export interface WireDeadSlowFinancial extends WireDeadSlowCommon {
  financial: true;
  source: WireValueSource;
  summary: WireDeadSlowSummaryCommon & { valueAtRisk: WireReconciliation };
  rows: WireDeadSlowFinancialRow[];
}
export type WireDeadSlow = WireDeadSlowUnits | WireDeadSlowFinancial;

export interface WireProfitRow {
  groupKey: string | null;
  sku: string | null;
  kind: 'line' | 'orphan' | 'direct_sale';
  facts: number;
  units: number;
  unknownCostUnits: number;
  unknownCostEvents: number;
  confirmedAfterConsumptionUnits: number;
  knownCostCents4: string;
  knownCostCents: number;
  revenue: WireMetric<{ cents4: string; cents: number }>;
  frozenRevenueCents4: string;
  margin: WireMetric<{ cents4: string; cents: number; percent: number | null }>;
  knownMarginCents4: string;
}
export interface WireProfitBucket {
  units: number;
  knownCostCents4: string;
  unknownCostUnits: number;
}
export interface WireProfitability {
  report: 'profitability';
  financial: true;
  source: WireValueSource;
  window: WireWindow;
  groupBy: 'part' | 'category' | 'booking';
  revenueBasis: 'frozen_invoice_lines';
  summary: {
    facts: number;
    totalKnownCost: WireReconciliation;
    unknownCostUnits: number;
    unknownCostEvents: number;
    frozenRevenue: WireReconciliation;
    knownMarginCents4: string;
    knownMarginCents: number;
    knownMarginPercent: number | null;
    unknownCostRevenueCents4: string;
    costWithoutRevenueCents4: string;
    orphan: WireProfitBucket;
    directSale: WireProfitBucket;
  };
  unattributedLegacyUnits: WireMetric<number>;
  unattributedLegacyEvents: WireMetric<number>;
  rows: WireProfitRow[];
  pagination: WirePagination;
}

export interface WireSampled<T> {
  metric: WireMetric<T>;
  sampleSize: number;
  insufficientSample: boolean;
  rankable: boolean;
}
export interface WireSupplier {
  supplierId: string;
  supplierName: string;
  orders: number;
  outcomes: { complete: number; complete_short_closed: number; held_unresolved: number; incomplete: number };
  onTime: WireSampled<{ percent: number | null; onTime: number; eligible: number }> & {
    noExpectedDate: number;
    completionTimeUnavailable: number;
    shortClosedExcluded: number;
  };
  fillRate: WireSampled<{ percent: number | null; filledUnits: number; expectedUnits: number }> & { overDeliveredUnits: number };
  rejectionRate: WireSampled<{
    percent: number | null;
    rejectedUnits: number;
    evaluatedUnits: number;
    observedCoveragePercent: number | null;
    observedLines: number;
    unobservedLines: number;
  }>;
  leadTime: WireSampled<{ medianDays: number | null; medianMicros: string; fromDispatch: number; fromOrderDate: number }> & {
    noReceiptEvidence: number;
    negativeInterval: number;
  };
  invoiceAccuracy: WireSampled<{ percent: number | null; matched: number; evaluated: number }> & {
    acceptedVariance: number;
    rejected: number;
    draft: number;
    nonEur: number;
    legacyHeaderInvoiceOrders: number;
  };
  /** Signed whole cents; present only with `includePriceVariance`. */
  priceVariance?: { openCents: number; openLines: number; acceptedCents: number; acceptedLines: number };
}
export interface WireSupplierPerformance {
  report: 'supplier-performance';
  financial: boolean;
  window: WireWindow;
  minimumSample: number;
  suppliers: WireSupplier[];
  pagination: WirePagination;
}

export type PriceKind = 'sell' | 'cost' | 'supplier' | 'paid';
export const PRICE_KINDS: readonly PriceKind[] = ['sell', 'cost', 'supplier', 'paid'];

export interface WireCatalogPoint {
  kind: 'sell' | 'cost';
  changedAt: string;
  changeSeq: number;
  changeType: string;
  oldCents4: string | null;
  newCents4: string;
  source: string;
  changedBy: string | null;
}
export interface WireSupplierPoint {
  kind: 'supplier';
  changedAt: string;
  changeSeq: number;
  changeType: string;
  supplierId: string;
  supplierPartId: string;
  oldCents4: string | null;
  newCents4: string;
  currency: string;
  source: string;
  changedBy: string | null;
}
export interface WirePaidPoint {
  kind: 'paid';
  changedAt: string;
  layerId: string;
  warehouseId: string;
  quantity: number;
  unitCostCents4: string | null;
  costStatus: string;
  purchaseOrderId: string | null;
  orderNumber: string | null;
  supplierId: string | null;
  provenance: 'fifo_layer';
}
export type WirePoint = WireCatalogPoint | WireSupplierPoint | WirePaidPoint;

export interface WireOpeningStream {
  supplierId: string;
  supplierPartId: string;
  currency: string;
}
export interface WireSeries {
  kind: PriceKind;
  points: WirePoint[];
  pagination: WirePagination;
  historyStartsAt: string | null;
  openingPrice: WireMetric<{ cents4: string; asOfEventAt: string; stream?: WireOpeningStream }> | null;
  openingPrices: (WireOpeningStream & { cents4: string; asOfEventAt: string })[] | null;
  streamCount: number | null;
  history: WireMetric<number>;
  clockInversions: number;
}
export interface WirePriceHistory {
  report: 'price-history';
  financial: boolean;
  partId: string;
  window: WireWindow | null;
  series: WireSeries[];
}

export interface WireValuationGroup {
  key: string;
  valueCents4: string;
  valueCents: number;
  knownQuantity: number;
  unconfirmedQuantity: number;
  unbackedQuantity: WireMetric<number>;
  availableKeys: number;
  unavailableKeys: number;
  unavailableReasons: Record<string, number>;
  complete: boolean;
}
export interface WireValuation {
  report: 'valuation';
  financial: true;
  source: WireValueSource;
  asOf: string;
  asOfKind: 'current' | 'historical';
  asOfRequest: { input: string | null; interpretation: 'current' | 'end_of_local_day' | 'exact_instant'; timeZone: string };
  costBasis: 'fifo';
  groupBy: 'part' | 'category' | 'warehouse';
  earliestBaselineAt: string | null;
  totals: WireReconciliation & {
    knownQuantity: number;
    unconfirmedQuantity: number;
    unbackedQuantity: WireMetric<number>;
    availableKeys: number;
    unavailableKeys: number;
    complete: boolean;
  };
  groups: WireValuationGroup[];
  pagination: WirePagination;
}

export interface ReportMap {
  abc: WireAbc;
  turnover: WireTurnover;
  'dead-and-slow': WireDeadSlow;
  profitability: WireProfitability;
  'supplier-performance': WireSupplierPerformance;
  'price-history': WirePriceHistory;
  valuation: WireValuation;
}
export type ReportKind = keyof ReportMap;
export const REPORT_KINDS: readonly ReportKind[] = [
  'abc',
  'turnover',
  'dead-and-slow',
  'profitability',
  'supplier-performance',
  'price-history',
  'valuation',
];

/** Query-string values exactly as they are sent: the portal never trims, clamps or coerces them on the way. */
export type ReportParams = Readonly<Record<string, string>>;

/* ------------------------------------------------------------------ permissions */

export interface ReportNeeds {
  financial: boolean;
  booking: boolean;
  party: boolean;
}

/**
 * What the variant a request asks for needs beyond `inventory.read`: the same table as the API's, with the same
 * defaults applied to an omitted parameter, so an omitted parameter is never taken for the unprivileged variant.
 */
export function reportNeeds(kind: ReportKind, params: ReportParams): ReportNeeds {
  const none: ReportNeeds = { financial: false, booking: false, party: false };
  switch (kind) {
    case 'abc':
    case 'turnover':
      return { ...none, financial: (params['basis'] ?? 'cost') !== 'units' };
    case 'dead-and-slow':
      return { ...none, financial: params['includeValue'] === 'true' };
    case 'profitability':
      return {
        financial: true,
        booking: params['bookingId'] !== undefined,
        party: params['partyId'] !== undefined || params['partyKind'] !== undefined,
      };
    case 'supplier-performance':
      return { ...none, financial: params['includePriceVariance'] === 'true' };
    case 'price-history':
      return { ...none, financial: (params['kind'] ?? 'sell').split(',').some((kindName) => kindName.trim() !== 'sell') };
    case 'valuation':
      return { ...none, financial: true };
  }
}

export type DeniedReason = 'signedOut' | 'read' | 'financial' | 'booking' | 'party';

/** Why this owner may not make the request, or `null` when it may. */
export function deniedReason(access: ReportsAccess, needs: ReportNeeds): DeniedReason | null {
  if (!access.active) return 'signedOut';
  if (!access.canRead) return 'read';
  if (needs.financial && !access.canFinancial) return 'financial';
  if (needs.booking && !access.canBookingFilter) return 'booking';
  if (needs.party && !access.canPartyFilter) return 'party';
  return null;
}

/* ------------------------------------------------------------------ keys and errors */

const BASE = '/inventory/reports';

export const reportKeys = {
  all: [...queryKeys.inventory.all, 'reports'] as const,
  report: (owner: string, kind: ReportKind, params: ReportParams) => [...reportKeys.all, owner, 'report', kind, params] as const,
  lookup: (owner: string, name: string, q: string) => [...reportKeys.all, owner, 'lookup', name, q] as const,
};

/** Position of the owner in a report query key. */
const OWNER_INDEX = reportKeys.all.length;

/** The stable `code`s of the report API (T14a section 7). The portal translates these, never the server's message. */
export const REPORT_ERROR_CODES = [
  'validation_error',
  'invalid_date',
  'invalid_range',
  'window_too_long',
  'window_in_future',
  'invalid_threshold',
  'invalid_pagination',
  'invalid_input',
  'part_not_found',
  'result_too_large',
  'tenant_timezone_unavailable',
  'report_timed_out',
  'report_failed',
  'report_serialization_failed',
] as const;

/** `errors.<key>` under the `inventoryReports` namespace for a failed report request. */
export function reportErrorKey(error: unknown): string {
  if (error instanceof OwnerChangedError) return 'errors.ownerChanged';
  const status = (error as { status?: unknown } | null)?.status;
  const code = (error as { data?: { code?: unknown } } | null)?.data?.code;
  if (typeof code === 'string' && (REPORT_ERROR_CODES as readonly string[]).includes(code)) return `errors.${code}`;
  if (status === 403) return 'errors.forbidden';
  if (status === 401 || status === 423) return 'errors.session';
  if (status === 404) return 'errors.notFound';
  if (typeof status === 'number' && status >= 500) return 'errors.report_failed';
  return 'errors.generic';
}

/* ------------------------------------------------------------------ owner and report hook */

/** The owner of the report queries of this render, and the clean-up of everyone else's. */
export function useReportsOwner(): { access: ReportsAccess; owner: string } {
  const access = useReportsAccess();
  const owner = ownerKey(access);
  useReportsCacheGuard(owner, reportKeys.all);
  return { access, owner };
}

/** Refuses, before anything is sent, a request whose owner is no longer the one that is signed in. */
function assertStillOwner(owner: string): void {
  if (ownerKey(readReportsAccess()) !== owner) throw new OwnerChangedError();
}

export interface InventoryReportResult<K extends ReportKind> {
  /** The answer for THESE params and this owner; `undefined` while there is none (also when denied). */
  data: ReportMap[K] | undefined;
  /** The answer on screen belongs to previous filters (kept only for the same owner and report) while the new one loads. */
  isStale: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  /** Why no request was made, or `null`. */
  denied: DeniedReason | null;
  refetch: () => void;
}

/**
 * One report. `params === null` means "nothing requested yet" (the user has not run it): no request. A request this
 * owner may not make is never sent and never shown, whatever is cached.
 */
export function useInventoryReport<K extends ReportKind>(kind: K, params: ReportParams | null): InventoryReportResult<K> {
  const { access, owner } = useReportsOwner();
  const denied = params === null ? null : deniedReason(access, reportNeeds(kind, params));
  const enabled = params !== null && denied === null;
  const query = useQuery({
    queryKey: reportKeys.report(owner, kind, params ?? {}),
    queryFn: () => {
      assertStillOwner(owner);
      return api.get<ReportMap[K]>(`${BASE}/${kind}`, { params: { ...params } });
    },
    enabled,
    // The previous answer keeps the screen steady while the next one loads, but only the previous answer of the SAME
    // owner and report (and, for a price history, of the same part), and it is marked stale so it is never read as
    // the answer to the new filters.
    placeholderData: (previous, previousQuery) => {
      if (previousQuery === undefined) return undefined;
      const key = previousQuery.queryKey;
      if (key[OWNER_INDEX] !== owner || key[OWNER_INDEX + 2] !== kind) return undefined;
      if (kind === 'price-history' && (key[OWNER_INDEX + 3] as ReportParams | undefined)?.['partId'] !== params?.['partId']) return undefined;
      return previous;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    data: enabled ? (query.data as ReportMap[K] | undefined) : undefined,
    isStale: enabled && query.isPlaceholderData,
    isLoading: enabled && query.isLoading,
    isFetching: enabled && query.isFetching,
    error: enabled ? query.error : null,
    denied,
    refetch: () => {
      void query.refetch();
    },
  };
}

/* ------------------------------------------------------------------ selector lookups */

export interface LookupOption {
  id: string;
  /** Always a name or a number a person recognises, never an id. */
  label: string;
}

export type LookupName = 'part' | 'supplier' | 'booking' | 'person' | 'company';

export const LOOKUP_PAGE_SIZE = 20;

interface LookupPage {
  items: LookupOption[];
  next: number | string | null;
}

interface CursorResponse<T> {
  data: T[];
  pagination: { nextCursor?: string | null; hasMore?: boolean };
}
interface OffsetResponse<T> {
  data: T[];
  pagination: { total: number };
}

const cursorNext = (r: CursorResponse<unknown>): string | null => (r.pagination.hasMore === true ? (r.pagination.nextCursor ?? null) : null);

function offsetNext(r: OffsetResponse<unknown>, offset: number): number | null {
  const next = offset + r.data.length;
  return r.data.length > 0 && next < r.pagination.total ? next : null;
}

type Fetcher = (q: string, page: number | string | null) => Promise<LookupPage>;

const FETCHERS: Record<LookupName, Fetcher> = {
  part: async (q, page) => {
    const offset = typeof page === 'number' ? page : 0;
    const r = await api.get<OffsetResponse<{ id: string; sku: string; name: string }>>('/inventory/parts', {
      params: { ...(q === '' ? {} : { q }), limit: LOOKUP_PAGE_SIZE, offset },
    });
    return { items: r.data.map((p) => ({ id: p.id, label: `${p.sku} · ${p.name}` })), next: offsetNext(r, offset) };
  },
  supplier: async (q, page) => {
    const r = await api.get<CursorResponse<{ id: string; name: string; code: string }>>('/inventory/suppliers', {
      params: { ...(q === '' ? {} : { q }), limit: LOOKUP_PAGE_SIZE, ...(typeof page === 'string' ? { cursor: page } : {}) },
    });
    return { items: r.data.map((s) => ({ id: s.id, label: `${s.name} (${s.code})` })), next: cursorNext(r) };
  },
  // The booking list has no text search (the route ignores `q`), so it is pageable, newest first, not searchable.
  booking: async (_q, page) => {
    const offset = typeof page === 'number' ? page : 0;
    const r = await api.get<OffsetResponse<{ id: string; workOrderNumber?: string | null; serviceType: string | null; scheduledAt: string }>>('/bookings', {
      params: { limit: LOOKUP_PAGE_SIZE, offset, sortBy: 'scheduledAt', sortDir: 'desc' },
    });
    return {
      items: r.data.map((b) => ({
        id: b.id,
        label: [b.workOrderNumber, b.serviceType, b.scheduledAt.slice(0, 10)].filter((part): part is string => typeof part === 'string' && part !== '').join(' · '),
      })),
      next: offsetNext(r, offset),
    };
  },
  person: async (q, page) => {
    const r = await api.get<CursorResponse<{ id: string; firstName: string; lastName: string }>>('/persons', {
      params: { ...(q === '' ? {} : { q }), limit: LOOKUP_PAGE_SIZE, ...(typeof page === 'string' ? { cursor: page } : {}) },
    });
    return { items: r.data.map((p) => ({ id: p.id, label: `${p.firstName} ${p.lastName}`.trim() })), next: cursorNext(r) };
  },
  company: async (q, page) => {
    const r = await api.get<CursorResponse<{ id: string; name: string }>>('/companies', {
      params: { ...(q === '' ? {} : { q }), limit: LOOKUP_PAGE_SIZE, ...(typeof page === 'string' ? { cursor: page } : {}) },
    });
    return { items: r.data.map((c) => ({ id: c.id, label: c.name })), next: cursorNext(r) };
  },
};

function lookupAllowed(access: ReportsAccess, name: LookupName): boolean {
  if (name === 'booking') return access.canBookingFilter;
  if (name === 'person' || name === 'company') return access.canPartyFilter;
  return access.canRead;
}

/** A searchable, pageable list for a selector: one page per request, owner-scoped, never the whole catalog. */
export function useReportLookup(name: LookupName, q: string, options: { enabled?: boolean } = {}) {
  const { access, owner } = useReportsOwner();
  const enabled = (options.enabled ?? true) && lookupAllowed(access, name);
  const query = useInfiniteQuery({
    queryKey: reportKeys.lookup(owner, name, q),
    queryFn: ({ pageParam }) => {
      assertStillOwner(owner);
      return FETCHERS[name](q, pageParam);
    },
    initialPageParam: null as number | string | null,
    getNextPageParam: (last) => last.next,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const options_ = enabled ? (query.data?.pages.flatMap((page) => page.items) ?? []) : [];
  return {
    options: options_,
    hasMore: enabled && query.hasNextPage,
    isFetching: enabled && query.isFetching,
    isError: enabled && query.isError,
    fetchMore: () => {
      void query.fetchNextPage();
    },
  };
}

export interface ReportCategory {
  id: string;
  name: string;
}
export interface ReportWarehouse {
  id: string;
  name: string;
  code: string;
}

/** The (small) category list, owner-scoped; its names label category groups. */
export function useReportCategories() {
  const { access, owner } = useReportsOwner();
  const enabled = access.canRead;
  const query = useQuery({
    queryKey: reportKeys.lookup(owner, 'categories', ''),
    queryFn: () => {
      assertStillOwner(owner);
      return api.get<{ data: ReportCategory[] }>('/inventory/categories');
    },
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return { options: enabled ? (query.data?.data ?? []) : [], isError: enabled && query.isError };
}

/** The (small) warehouse list, owner-scoped; its names label warehouse groups. */
export function useReportWarehouses() {
  const { access, owner } = useReportsOwner();
  const enabled = access.canRead;
  const query = useQuery({
    queryKey: reportKeys.lookup(owner, 'warehouses', ''),
    queryFn: () => {
      assertStillOwner(owner);
      return api.get<{ data: ReportWarehouse[] }>('/inventory/warehouses');
    },
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return { options: enabled ? (query.data?.data ?? []) : [], isError: enabled && query.isError };
}
